import { Button } from '@heroui/react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import CopyButton from 'components/Hero/CopyButton'
import { useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import { $fetch } from 'utils/fetch'
import AudioWaveform from './AudioWaveform'
import EffectsTimeline from './EffectsTimeline'
import ReadScriptButton from './ReadScriptButton'
import TaggedTextEditor from './TaggedTextEditor'
import { supportedEffects, useSamples } from './utils'

export default function SampleEditor({ selectedCloneId, selectedSampleId }) {
  const queryClient = useQueryClient()
  const { data: samples } = useSamples(selectedCloneId)
  const selectedSample = useMemo(() => samples.find(s => s.id === selectedSampleId), [selectedSampleId, samples])

  const [transcript, setTranscript] = useState(selectedSample.transcription)
  const isEdited = useMemo(() => transcript !== selectedSample.transcription, [transcript, selectedSample.transcription])

  const { data: sampleUrl, isLoading: isLoadingSample } = useQuery({
    queryKey: ['sampleUrl', selectedCloneId, selectedSample.id],
    queryFn: async () => {
      const response = await $fetch(`/v1/voices/${selectedCloneId}/samples/${selectedSample.id}`, { responseType: 'blob' })
      return URL.createObjectURL(response)
    },
    enabled: !!selectedSample,
    staleTime: Infinity,
  })

  const { mutate: saveSampleTranscription, isLoading: isSavingSample } = useMutation({
    mutationFn: async () => {
      const response = await $fetch(`/v1/voices/${selectedCloneId}/samples`, {
        method: 'PATCH',
        body: { samples: [{ id: selectedSample.id, transcription: transcript }] },
      })
      return response
    },
    onSuccess: () => {
      toast.success('Sample transcription saved')
      queryClient.setQueryData(['samples', selectedCloneId], samples =>
        samples.map(s =>
          s.id === selectedSample.id
            ? { ...s, transcription: transcript }
            : s,
        ))
    },
    onError: () => {
      toast.error('Error saving sample transcription')
    },
  })

  useEffect(() => {
    setTranscript(selectedSample.transcription)
  }, [selectedSample])

  return (
    <div className="flex w-full flex-col gap-6 p-6 pb-8">
      <div className="flex flex-col items-start border-b border-gray-200 pb-4">
        <div className="mb-3 flex w-full items-start justify-between gap-2">
          <CopyButton
            value={selectedSample.id}
            displayText={`${selectedSample.id.slice(0, 8)}...`}
            size="md"
            color="default"
            variant="light"
            className="mr-auto font-medium!"
            radius="sm"
          />
          <ReadScriptButton />
        </div>
        <p className="text-sm font-light text-gray-500">
          Each sample teaches our LLM-based TTS system how your voice sounds when saying specific words and phrases.
          <br />
          Accurate transcription is crucial—it helps the system align audio patterns with text, enabling better style transfer and expressiveness.
        </p>
      </div>

      <AudioWaveform
        url={sampleUrl}
        isLoading={isLoadingSample}
      />

      <div className="flex w-full flex-col">
        <label className="mb-2 block text-sm font-medium">Transcription</label>
        <TaggedTextEditor
          variant="bordered"
          radius="sm"
          value={transcript}
          onValueChange={setTranscript}
          placeholder="Enter transcription for this audio sample..."
          supportedEffects={supportedEffects}
          description={(
            <p className="text-xs text-gray-500">
              Type
              {' '}
              <kbd className={`
                rounded border border-gray-300 bg-gray-100 px-1 py-0.5 text-2xs
              `}
              >
                &lt;
              </kbd>
              {' '}
              to insert effects
            </p>
          )}
        />

        {isEdited && (
          <Button
            variant="flat"
            radius="sm"
            size="sm"
            color="primary"
            onPress={saveSampleTranscription}
            isLoading={isSavingSample}
            className="mt-2 ml-auto w-fit"
          >
            Save
          </Button>
        )}
      </div>

      {transcript && (
        <div>
          <EffectsTimeline text={transcript} />
          <p className="pt-2 text-xs text-gray-500">
            Adding effect tags to sample transcriptions helps the AI learn to express different styles and emotions.
          </p>
        </div>
      )}
    </div>
  )
}
